import { COUNTRY_CURRENCIES } from './countryCurrencyData.js';

const countryCurrencies = new Map(COUNTRY_CURRENCIES.flatMap(([iso2, iso3, ...currencies]) =>
    [iso2, iso3].filter(Boolean).map(code => [code, currencies] as const)));
const currencyNames = new Intl.DisplayNames(['en'], { type: 'currency' });

export function getCountryCurrency(countryCode: unknown): { name: string; symbol: string } | null {
    if (typeof countryCode !== 'string')
        return null;
    const code = countryCode.trim().toUpperCase();
    const currencies = countryCurrencies.get(code);
    if (!currencies?.length)
        return null;
    return {
        name: currencies.map(currency => currencyNames.of(currency) || currency).join(' / '),
        symbol: currencies.map(currency => new Intl.NumberFormat('en', { style: 'currency', currency })
            .formatToParts(0).find(part => part.type === 'currency')?.value || currency).join(' / ')
    };
}

export function validCountryCurrency(value: unknown): string | null {
    if (typeof value !== 'string')
        return null;
    const currency = value.trim();
    return currency && !/^(?:N\/A|Data unavailable|Unknown|Not available|Insufficient current intelligence)(?:\s*\([^)]*\))?$/i.test(currency)
        && !/USE VALUE FROM STRUCTURED DATA/i.test(currency) ? currency : null;
}

export function retainCountryCurrency(worldBank: any, economic: any, previous: any, countryCode?: string) {
    const verified = validCountryCurrency(economic?.currency) ? null : getCountryCurrency(countryCode);
    if (!validCountryCurrency(worldBank?.currency) && verified)
        worldBank = { ...(worldBank || {}), currency: verified.name, currencySymbol: verified.symbol };
    const current = validCountryCurrency(worldBank?.currency);
    const priorBank = previous?.quantitativeIndicators?.worldBank;
    const prior = validCountryCurrency(priorBank?.currency);
    const selectedBank = current ? worldBank : prior
        ? { ...(worldBank || {}), currency: prior, currencySymbol: priorBank.currencySymbol || 'N/A' }
        : worldBank ?? null;
    const selected = validCountryCurrency(selectedBank?.currency);
    const symbol = validCountryCurrency(selectedBank?.currencySymbol);
    const label = selected ? symbol && !selected.endsWith(`(${symbol})`) ? `${selected} (${symbol})` : selected : null;
    const previousLabel = validCountryCurrency(previous?.economicIndicators?.currency);
    const economicLabel = label || validCountryCurrency(economic?.currency) || previousLabel;
    const selectedEconomic = economicLabel
        ? { ...(economic && typeof economic === 'object' ? economic : {}), currency: economicLabel }
        : economic;
    return { worldBank: selectedBank, economicIndicators: selectedEconomic };
}
